import { SxProps, Theme } from '@mui/material'

/**
 * Shared admin styling constants.
 * Every admin page should compose from these to ensure visual consistency.
 */

// ── Colors ──────────────────────────────────────────────────────────
export const adminColors = {
  accent: '#18BB0E',
  accentHover: 'rgba(24, 187, 14, 0.08)',
  accentBorder: 'rgba(24, 187, 14, 0.3)',
  pageBg: '#f5f6f7',
  cardBg: '#ffffff',
  headerBg: '#fafafa',
  border: '#e2e5e9',
  borderLight: '#eef0f2',
  textPrimary: '#1a1d21',
  textSecondary: '#616770',
  textMuted: '#8c939b',
  profitGreen: '#1b8a2a',
  lossRed: '#c9372c',
  warningAmber: '#b8860b',
} as const

// ── Page-level layout ───────────────────────────────────────────────
export const pageContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2.5,
  minHeight: 0,
}

// ── Page header: title + actions row ────────────────────────────────
export const pageHeader: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 1.5,
}

export const pageTitle: SxProps<Theme> = {
  fontSize: '1.35rem',
  fontWeight: 600,
  color: adminColors.textPrimary,
  letterSpacing: '-0.01em',
}

export const pageSubtitle: SxProps<Theme> = {
  fontSize: '0.8rem',
  color: adminColors.textSecondary,
}

// ── Section containers ──────────────────────────────────────────────
export const sectionCard: SxProps<Theme> = {
  bgcolor: adminColors.cardBg,
  border: `1px solid ${adminColors.border}`,
  borderRadius: '8px',
  overflow: 'hidden',
}

export const sectionHeader: SxProps<Theme> = {
  px: 2.5,
  py: 1.5,
  borderBottom: `1px solid ${adminColors.borderLight}`,
  bgcolor: adminColors.headerBg,
}

export const sectionTitle: SxProps<Theme> = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: adminColors.textPrimary,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
}

export const sectionBody: SxProps<Theme> = {
  p: 2.5,
}

// ── Filter / toolbar bars ───────────────────────────────────────────
export const filterBar: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
  px: 2.5,
  py: 1.5,
  bgcolor: adminColors.headerBg,
  borderBottom: `1px solid ${adminColors.borderLight}`,
}

// ── Table styling ───────────────────────────────────────────────────
export const tableHeadCell: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  color: adminColors.textSecondary,
  bgcolor: adminColors.headerBg,
  borderBottom: `1px solid ${adminColors.border}`,
  whiteSpace: 'nowrap',
  py: 1.5,
}

export const tableBodyCell: SxProps<Theme> = {
  fontSize: '0.825rem',
  color: adminColors.textPrimary,
  borderBottom: `1px solid ${adminColors.borderLight}`,
  py: 1.2,
}

// ── Buttons ─────────────────────────────────────────────────────────
export const primaryButton: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.8rem',
  bgcolor: adminColors.accent,
  '&:hover': { bgcolor: '#15a30c' },
  borderRadius: '6px',
  px: 2,
  py: 0.75,
  boxShadow: 'none',
}

export const outlinedButton: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.8rem',
  color: adminColors.textSecondary,
  borderColor: adminColors.border,
  borderRadius: '6px',
  px: 2,
  py: 0.75,
  '&:hover': {
    borderColor: adminColors.accent,
    color: adminColors.accent,
    bgcolor: adminColors.accentHover,
  },
}

// ── Stat display (for summary numbers) ──────────────────────────────
export const statValue: SxProps<Theme> = {
  fontSize: '1.5rem',
  fontWeight: 700,
  color: adminColors.textPrimary,
  lineHeight: 1,
}

export const statLabel: SxProps<Theme> = {
  fontSize: '0.7rem',
  fontWeight: 500,
  color: adminColors.textMuted,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  mt: 0.5,
}

// ── Empty state ─────────────────────────────────────────────────────
export const emptyState: SxProps<Theme> = {
  ...sectionCard,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: 6,
  px: 3,
}

// ── Loading state ───────────────────────────────────────────────────
export const loadingContainer: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '300px',
}
